import type { UserRole } from '@/types/models';

/**
 * Modules a user may be given (0055). The server holds the same list in
 * app.module_catalogue() and enforces it; this decides only what the app
 * offers. Keep the two identical.
 */
export const MODULES = [
  { key: 'dashboard', label: 'Dashboard' },
  { key: 'diary', label: 'Daily diary' },
  { key: 'fleet', label: 'Fleet' },
  { key: 'production', label: 'Production' },
  { key: 'blasting', label: 'Blasting' },
  { key: 'breakdowns', label: 'Breakdowns' },
  { key: 'vehicle_checks', label: 'Vehicle checks' },
  { key: 'fuel', label: 'Fuel' },
  { key: 'workshop', label: 'Workshop' },
  { key: 'maintenance', label: 'Maintenance' },
  { key: 'parts', label: 'Parts & stock' },
  { key: 'targets', label: 'Targets' },
  { key: 'reports', label: 'Reports' },
  { key: 'company', label: 'Company' },
  { key: 'concrete', label: 'Concrete' },
  { key: 'brick', label: 'Brick Plant' },
  { key: 'precast', label: 'Pre-cast' },
  { key: 'administration', label: 'Administration' },
] as const;

export type ModuleKey = (typeof MODULES)[number]['key'];

const ALL: ModuleKey[] = MODULES.map((m) => m.key);

const QUARRY_BY_ROLE: Record<UserRole, ModuleKey[]> = {
  operator: ['dashboard', 'diary', 'fleet', 'breakdowns', 'vehicle_checks', 'fuel'],
  supervisor: ['dashboard', 'diary', 'fleet', 'production', 'blasting', 'breakdowns',
    'vehicle_checks', 'fuel', 'reports'],
  maintenance: ['dashboard', 'diary', 'fleet', 'breakdowns', 'vehicle_checks', 'fuel',
    'workshop', 'maintenance', 'parts'],
  // 0066: the Brick Plant and Pre-cast are ticked per user, never by default.
  manager: ALL.filter((m) => m !== 'brick' && m !== 'precast'),
  admin: ALL,
};

const BRANCH: ModuleKey[] = ['concrete', 'fleet', 'vehicle_checks', 'breakdowns', 'fuel'];

/** Mirror of app.default_modules(): today's menu for the role, by where
 *  the user works. */
export function defaultModules(role: UserRole, quarry: boolean, branch: boolean): ModuleKey[] {
  const set = new Set<ModuleKey>([
    ...(quarry ? QUARRY_BY_ROLE[role] ?? [] : []),
    ...(branch ? BRANCH : []),
  ]);
  return ALL.filter((m) => set.has(m));
}

/** The module a route belongs to; undefined = always open (help, 404). */
export function moduleForPath(path: string): ModuleKey | undefined {
  const p = path.replace(/\/+$/, '') || '/';
  if (p === '/') return 'dashboard';
  const rules: [RegExp, ModuleKey][] = [
    [/^\/diary/, 'diary'],
    [/^\/machines/, 'fleet'],
    [/^\/fleet\/branch/, 'fleet'],
    [/^\/fleet\/checks/, 'vehicle_checks'],
    [/^\/fleet\/breakdowns/, 'breakdowns'],
    [/^\/production/, 'production'],
    [/^\/blasts/, 'blasting'],
    [/^\/breakdowns/, 'breakdowns'],
    [/^\/fuel/, 'fuel'],
    [/^\/workshop/, 'workshop'],
    [/^\/maintenance/, 'maintenance'],
    [/^\/parts/, 'parts'],
    [/^\/targets/, 'targets'],
    [/^\/reports/, 'reports'],
    [/^\/company/, 'company'],
    [/^\/concrete/, 'concrete'],
    [/^\/brick/, 'brick'],
    [/^\/precast/, 'precast'],
    [/^\/admin/, 'administration'],
  ];
  return rules.find(([re]) => re.test(p))?.[1];
}

/** What my_access() returns (0055). */
export interface Access {
  all_sites: boolean;
  /** Every site this user may see. */
  sites: string[];
  /** Sites the user is explicitly assigned to (All branches not counted). */
  member_sites: string[];
  sees_quarry: boolean;
  /** Effective modules: the custom list, or the role's defaults. */
  modules: ModuleKey[];
  custom_modules: boolean;
}

/** Until the server has answered (first start offline on an old cache):
 *  what the profile alone says, which is how the app behaved before 0055. */
export function fallbackAccess(role: UserRole, homeSite: string | null | undefined): Access {
  const branch = !!homeSite;
  return {
    all_sites: !branch,
    sites: branch ? [homeSite as string] : [],
    member_sites: branch ? [homeSite as string] : [],
    sees_quarry: !branch,
    modules: defaultModules(role, !branch, branch),
    custom_modules: false,
  };
}

